import { doc, getDoc, setDoc, updateDoc, serverTimestamp, addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../sdk_initialization';

const COLLECTION = 'vendors';
const DIR_COLLECTION = 'vendorDirectory';

const normalizeBusinessName = (value) => String(value || '').trim().toLowerCase().replace(/[\s._'-]+/g, '');

/**
 * Generate the next vendor number in format 00101, 00102, etc.
 * Uses a counter document approach.
 */
const generateNextVendorNumber = async () => {
  const counterRef = doc(db, 'counters', 'vendorNumber');
  
  try {
    const counterSnap = await getDoc(counterRef);
    let nextNumber = 101; // Start from 00101
    
    if (counterSnap.exists()) {
      const current = counterSnap.data().current || 100;
      nextNumber = current + 1;
    }
    
    // Update the counter
    try {
      await setDoc(counterRef, { current: nextNumber, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn('Could not update vendor number counter:', e);
    }
    
    return String(nextNumber).padStart(5, '0');
  } catch (e) {
    console.error('Error generating vendor number:', e);
    // Fallback to timestamp-based number if counter fails
    const timestamp = Date.now();
    const randomPart = Math.floor(Math.random() * 1000);
    return String((timestamp % 100000) + randomPart).padStart(5, '0');
  }
};

export const getVendorDirectoryEntry = async (vendorId) => {
  if (!vendorId) return null;
  const ref = doc(db, DIR_COLLECTION, vendorId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  return {
    id: snap.id,
    name: data.name || '',
    email: data.email || '',
    type: data.type || '',
    vendorNumber: data.vendorNumber || '',
    city: data.city || '',
    state: data.state || '',
  };
};

export const getVendor = async (vendorId) => {
  if (!vendorId) throw new Error('Missing vendorId for getVendor');
  const ref = doc(db, COLLECTION, vendorId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  try {
    // Normalize to new field names
    const normalized = {
      id: snap.id,
      ...data,
      name: data.name ?? data.businessName ?? '',
      type: data.type ?? data.businessType ?? '',
      email: data.email ?? data.businessEmail ?? '',
      phoneNumber: data.phoneNumber ?? data.businessPhone ?? '',
      description: data.description ?? data.businessDescription ?? '',
      federalId: data.federalId ?? data.businessFederalId ?? '',
      addressStreet: data.addressStreet ?? data.businessAddressStreet ?? '',
      addressCity: data.addressCity ?? data.businessAddressCity ?? '',
      addressState: data.addressState ?? data.businessAddressState ?? '',
      addressZip: data.addressZip ?? data.businessAddressZip ?? '',
      countryCode: data.countryCode ?? data.businessAreaCode ?? '+1',
    };
    return normalized;
  } catch (e) {
    // If permission denied for full vendor doc, try to read the public vendor directory entry
    if (e?.code === 'permission-denied' || (e?.message && e.message.toLowerCase().includes('permission'))) {
      try {
        const dirRef = doc(db, DIR_COLLECTION, vendorId);
        const dirSnap = await getDoc(dirRef);
        if (!dirSnap.exists()) return null;
        const d = dirSnap.data() || {};
        return {
          id: dirSnap.id,
          name: d.name || '',
          type: d.type || d.businessType || '',
          vendorNumber: d.vendorNumber || '',
          addressCity: d.city || '',
          addressState: d.state || '',
        };
      } catch (err) {
        // fall through
      }
    }
    throw e;
  }
};

export const upsertVendor = async (vendorId, data) => {
  // Accept both old and new keys; write only new keys
  const fields = {
    name: (data.name ?? data.businessName) || '',
    type: (data.type ?? data.businessType) || '',
    email: (data.email ?? data.businessEmail) || '',
    // New canonical fields: store local phone digits and business country/area code separately
    phoneNumber: (data.phoneNumber ?? data.businessPhone ?? '') || '',
    businessAreaCode: (data.businessAreaCode ?? data.countryCode ?? '+1') || '+1',
    description: (data.description ?? data.businessDescription) || '',
    federalId: (data.federalId ?? data.businessFederalId) || '',
    addressStreet: (data.addressStreet ?? data.businessAddressStreet) || '',
    addressCity: (data.addressCity ?? data.businessAddressCity) || '',
    addressState: (data.addressState ?? data.businessAddressState) || '',
    addressZip: (data.addressZip ?? data.businessAddressZip) || '',
    countryName: (data.countryName ?? data.businessCountryName) || '',
  };
  const normalizedName = normalizeBusinessName(fields.name);
  const nameProvided = (data.name !== undefined || data.businessName !== undefined);

  if (nameProvided && fields.name.trim().length > 30) {
    return {
      __error: {
        code: 'vendor-name-too-long',
        message: 'Business name must be 30 characters or fewer.',
      },
    };
  }

  if (nameProvided && fields.name.trim().includes('_')) {
    return {
      __error: {
        code: 'vendor-name-invalid-chars',
        message: "Business name cannot include underscore (_). Use letters, numbers, spaces, and these symbols: . - ' &.",
      },
    };
  }

  if (nameProvided && fields.name.trim() && !/^[A-Za-z0-9 .'&\-]+$/.test(fields.name.trim())) {
    return {
      __error: {
        code: 'vendor-name-invalid-chars',
        message: "Business name can contain only English letters, numbers, spaces, and these symbols: . - ' &.",
      },
    };
  }

  // Prevent duplicates across create and update when caller provides a business name.
  if (nameProvided && normalizedName) {
    const existing = await findVendorByName(fields.name, vendorId || null);
    if (existing && existing.id && (!vendorId || existing.id !== vendorId)) {
      return {
        __error: {
          code: 'duplicate-vendor-name',
          message: 'A vendor with this business name already exists. Please use the existing vendor record.',
          existingVendorId: existing.id,
        },
      };
    }
  }

  // Update existing vendor
  if (vendorId) {
    const ref = doc(db, COLLECTION, vendorId);
    
    // Check if vendor exists and has vendorNumber
    let existingVendor = null;
    try {
      const snap = await getDoc(ref);
      if (snap.exists()) {
        existingVendor = snap.data();
      }
    } catch (e) {
      // Vendor doesn't exist yet
    }
    
    // Only include fields that were explicitly provided in the `data`
    // argument to avoid overwriting existing vendor values with empty
    // strings when the caller did not intend to change them.
    const updateData = { updatedAt: serverTimestamp() };
    const provided = (keyNew, keyOld) => (data[keyNew] !== undefined || data[keyOld] !== undefined);
    if (provided('name', 'businessName')) updateData.name = fields.name;
    if (provided('name', 'businessName')) updateData.nameNormalized = normalizedName;
    if (provided('type', 'businessType')) updateData.type = fields.type;
    if (provided('email', 'businessEmail')) updateData.email = fields.email;
    // Accept explicit phoneNumber and countryCode updates. Always store them
    // separately and do not compose a legacy `phone` field.
    if (provided('phoneNumber', 'businessPhone')) updateData.phoneNumber = fields.phoneNumber;
    if (provided('businessAreaCode', 'countryCode')) updateData.businessAreaCode = fields.businessAreaCode;
    if (provided('description', 'businessDescription')) updateData.description = fields.description;
    if (provided('federalId', 'businessFederalId')) updateData.federalId = fields.federalId;
    if (provided('addressStreet', 'businessAddressStreet')) updateData.addressStreet = fields.addressStreet;
    if (provided('addressCity', 'businessAddressCity')) updateData.addressCity = fields.addressCity;
    if (provided('addressState', 'businessAddressState')) updateData.addressState = fields.addressState;
    if (provided('addressZip', 'businessAddressZip')) updateData.addressZip = fields.addressZip;
    // (phoneNumber and countryCode handled above)
    if (provided('countryName', 'businessCountryName')) updateData.countryName = fields.countryName;
    if (Array.isArray(data.memberUids)) updateData.memberUids = data.memberUids;
    
    // Preserve or generate vendorNumber
    if (existingVendor?.vendorNumber) {
      updateData.vendorNumber = existingVendor.vendorNumber;
    } else if (data.vendorNumber) {
      updateData.vendorNumber = data.vendorNumber;
    } else {
      updateData.vendorNumber = await generateNextVendorNumber();
    }
    
    // Preserve vendorStatus or set from data, default to existing or 'Active'
    if (data.vendorStatus) {
      updateData.vendorStatus = data.vendorStatus;
    } else if (existingVendor?.vendorStatus) {
      updateData.vendorStatus = existingVendor.vendorStatus;
    } else {
      updateData.vendorStatus = 'Active';
    }
    
    // Preserve vendorAdmin if it exists
    if (existingVendor?.vendorAdmin !== undefined) {
      updateData.vendorAdmin = existingVendor.vendorAdmin;
    } else if (data.vendorAdmin !== undefined) {
      updateData.vendorAdmin = data.vendorAdmin;
    }
    
    try {
      await updateDoc(ref, updateData);
    } catch (e) {
      console.error('vendorsService.upsertVendor updateDoc failed, attempting setDoc', e);
      try {
        await setDoc(ref, { ...updateData, createdAt: serverTimestamp() }, { merge: true });
      } catch (se) {
        console.error('vendorsService.upsertVendor setDoc fallback failed', se);
        se.message = `[vendorsService.upsertVendor:update] ${se.code || ''} ${se.message || se}`;
        throw se;
      }
    }
    // Sync directory entry (public name index)
    try {
      await setDoc(doc(db, DIR_COLLECTION, vendorId), {
        vendorId,
        vendorNumber: updateData.vendorNumber,
        name: fields.name || '',
        nameNormalized: normalizedName,
        type: fields.type || '',
        email: fields.email || '',
        city: fields.addressCity || '',
        state: fields.addressState || '',
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch {}
    try {
      const updated = await getDoc(ref);
      if (updated.exists()) return { id: updated.id, ...updated.data() };
    } catch {}
    return { id: vendorId, ...fields, vendorNumber: updateData.vendorNumber };
  }

  // Create new vendor with auto-generated ID and vendorNumber
  const vendorNumber = data.vendorNumber || await generateNextVendorNumber();

    const createData = {
      ...fields,
    nameNormalized: normalizedName,
    vendorNumber,
    vendorStatus: data.vendorStatus || 'Active',
    vendorAdmin: data.vendorAdmin || data.createdByUid || (Array.isArray(data.memberUids) && data.memberUids.length > 0 ? data.memberUids[0] : null),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    memberUids: Array.isArray(data.memberUids) ? data.memberUids : [],
  };
  const col = collection(db, COLLECTION);
  let newRef = null;
  try {
    newRef = await addDoc(col, createData);
  } catch (e) {
    console.error('vendorsService.upsertVendor addDoc failed', e);
    return { __error: { code: e.code || '', message: `[vendorsService.upsertVendor:add] ${e.message || e}` } };
  }
  // Create directory entry
  try {
    await setDoc(doc(db, DIR_COLLECTION, newRef.id), {
      vendorId: newRef.id,
      vendorNumber,
      name: fields.name || '',
      nameNormalized: normalizedName,
      type: fields.type || '',
      email: fields.email || '',
      city: createData.addressCity || '',
      state: createData.addressState || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (e) {
    console.error('vendorsService.upsertVendor setDoc vendorDirectory failed', e);
    // don't fail the entire create, but return a structured error for visibility
    return { __error: { code: e.code || '', message: `[vendorsService.upsertVendor:dir] ${e.message || e}` } };
  }
  try {
    const created = await getDoc(newRef);
    if (created.exists()) return { id: newRef.id, ...created.data() };
  } catch (e) {
    console.error('vendorsService.upsertVendor getDoc after create failed', e);
    return { __error: { code: e.code || '', message: `[vendorsService.upsertVendor:get] ${e.message || e}` } };
  }
  return { id: newRef.id, ...createData };
};

export const listVendors = async () => {
  const col = collection(db, COLLECTION);
  const snap = await getDocs(col);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Lightweight vendor list for client-side autocomplete: return only small fields
export const listVendorsLight = async () => {
  // Prefer reading the public vendor directory which is smaller and often
  // granted read access to unauthenticated/regular users via rules.
  try {
    const dirCol = collection(db, DIR_COLLECTION);
    const snap = await getDocs(dirCol);
    return snap.docs.map(d => {
      const data = d.data() || {};
      return {
        id: d.id,
        vendorNumber: data.vendorNumber || '',
        name: data.name || '',
        type: data.type || '',
        email: data.email || '',
        city: data.city || '',
        state: data.state || '',
      };
    });
  } catch (e) {
    // If reading the directory fails (rules), fall back to reading vendors
    // This may also fail depending on rules; callers should handle permission errors.
    const col = collection(db, COLLECTION);
    const snap = await getDocs(col);
    return snap.docs.map(d => {
      const data = d.data() || {};
      return {
        id: d.id,
        vendorNumber: data.vendorNumber || data.vendorNumber || '',
        name: data.name ?? data.businessName ?? '',
        type: data.type ?? data.businessType ?? '',
        email: data.email ?? data.businessEmail ?? '',
        city: data.addressCity ?? data.businessAddressCity ?? '',
        state: data.addressState ?? data.businessAddressState ?? '',
      };
    });
  }
};
export const listVendorsForUser = async (uid) => {
  if (!uid) return [];
  const col = collection(db, COLLECTION);
  const q = query(col, where('memberUids', 'array-contains', uid));
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const data = d.data() || {};
    return {
      id: d.id,
      ...data,
      name: data.name ?? data.businessName ?? '',
      type: data.type ?? data.businessType ?? '',
        email: data.email ?? data.businessEmail ?? '',
        phoneNumber: data.phoneNumber ?? data.businessPhone ?? '',
        phoneNumber: data.phoneNumber ?? data.businessPhone ?? '',
      description: data.description ?? data.businessDescription ?? '',
      federalId: data.federalId ?? data.businessFederalId ?? '',
      addressStreet: data.addressStreet ?? data.businessAddressStreet ?? '',
      addressCity: data.addressCity ?? data.businessAddressCity ?? '',
      addressState: data.addressState ?? data.businessAddressState ?? '',
      addressZip: data.addressZip ?? data.businessAddressZip ?? '',
        countryCode: data.countryCode ?? data.businessAreaCode ?? '+1',
    };
  });
};

/**
 * Check if a business name already exists in the vendors collection
 * Returns the vendor document if found, null otherwise
 */
export const findVendorByName = async (businessName, excludeVendorId = null) => {
  if (!businessName) return null;
  const normalizedName = normalizeBusinessName(businessName);
  // First try normalized lookup in the public vendor directory.
  try {
    const dirCol = collection(db, DIR_COLLECTION);
    const q = query(dirCol, where('nameNormalized', '==', normalizedName));
    const snap = await getDocs(q);
    for (const d of snap.docs) {
      if (excludeVendorId && d.id === excludeVendorId) continue;
      const data = d.data() || {};
      return { id: d.id, ...data };
    }
  } catch (e) {
    // ignore and try legacy fallback
  }

  // Legacy fallback for older records that do not have nameNormalized yet.
  try {
    const dirCol = collection(db, DIR_COLLECTION);
    const allSnap = await getDocs(dirCol);
    for (const d of allSnap.docs) {
      if (excludeVendorId && d.id === excludeVendorId) continue;
      const data = d.data() || {};
      if (normalizeBusinessName(data?.name) === normalizedName) {
        return { id: d.id, ...data };
      }
    }
  } catch (e) {
    // ignore and try vendors fallback
  }

  // Fallback: query vendors collection by normalized name.
  try {
    const col = collection(db, COLLECTION);
    const q2 = query(col, where('nameNormalized', '==', normalizedName));
    const snap2 = await getDocs(q2);
    for (const docSnap of snap2.docs) {
      if (excludeVendorId && docSnap.id === excludeVendorId) continue;
      const data = docSnap.data() || {};
      return { id: docSnap.id, ...data };
    }
    return null;
  } catch (e) {
    // If the client is not allowed to read full vendor docs, treat as not found
    // and return null so callers can decide whether to create or surface
    // a permission error themselves. This avoids noisy console warnings
    // and accidental behavior when rules prevent the lookup.
    if (e?.code === 'permission-denied' || (e?.message && e.message.toLowerCase().includes('permission'))) {
      return null;
    }
    throw e;
  }
};
